import Controller from '@ember/controller';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import type { TableLayoutSettings } from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import type HotelSettingsBudgetDetailRoute from 'lumen/routes/hotel/settings/budget/detail';
import { LayoutSettingsHandler } from 'lumen/utils/budget-forecast/layout-settings';
import {
  DATA_TYPES,
  DATE_GRANULARITY,
  type DateGranularity,
  type Variance,
  VARIANCES,
} from 'lumen/utils/constants/budget-forecast-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export default class HotelSettingsBudgetDetailController extends Controller {
  declare model: ModelFrom<HotelSettingsBudgetDetailRoute>;
  queryParams = ['granularity', 'startDate', 'endDate', 'variance'];

  @service declare alerting: Services['alerting'];
  @service declare intl: Services['intl'];

  @tracked endDate: string = dayjs().endOf('year').format('YYYY-MM-DD');
  @tracked expandedRowIds: string[] = [DATA_TYPES.BUDGET];
  @tracked granularity: DateGranularity = DATE_GRANULARITY.MONTH;
  @tracked layoutSettings: TableLayoutSettings = {
    dataTypesOrder: [],
    hiddenDataTypes: [],
  };
  @tracked startDate: string = dayjs().startOf('year').format('YYYY-MM-DD');
  @tracked variance: Variance = VARIANCES.NONE;
  @tracked versionId: string | undefined;

  #layoutSettingsHandler: LayoutSettingsHandler;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get budgetForecastItems() {
    return this.model.dataItems.last?.value?.dataItems ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasUnsavedChanges() {
    return this.budgetForecastItems.some(
      (item) => item.hasUndistributedChanges || item.hasUnsavedChanges,
    );
  }

  constructor(owner: Owner) {
    super(owner);
    this.#layoutSettingsHandler = new LayoutSettingsHandler(owner);
  }

  discardChanges(): void {
    this.budgetForecastItems.forEach((item) => {
      item.rollbackAttributes();
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  showDiscardChangesAlert() {
    return this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('general.unsaved_changes.title'),
      text: this.intl.t(
        'settings.budget_forecast.leave_page_alert.description',
      ),
      buttons: {
        cancel: {
          text: this.intl.t(
            'settings.budget_forecast.leave_page_alert.cta.cancel',
          ),
        },
        confirm: {
          text: this.intl.t(
            'settings.budget_forecast.leave_page_alert.cta.confirm',
          ),
        },
      },
    });
  }

  @action doRefresh(): void {
    this.send('refreshRoute');
  }

  @action onCollapseAllRows(): void {
    this.expandedRowIds = [];
  }

  @action onRowCollapseToggle(rowId: string): void {
    if (this.expandedRowIds.includes(rowId)) {
      this.expandedRowIds = this.expandedRowIds.filter((id) => id !== rowId);
    } else {
      this.expandedRowIds = [...this.expandedRowIds, rowId];
    }
  }

  @action updateLayoutSettings(layoutSettings: TableLayoutSettings): void {
    this.#layoutSettingsHandler.setLayoutSettings({
      view: 'budget',
      mode: 'edit',
      layoutSettings,
    });

    this.layoutSettings = layoutSettings;
  }
}
